import { WORKFLOW_FIRST_PATH, WORKFLOW_LIMITS, WORKFLOW_NOTES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { addNote, canAddNote, editNote, notesAt, removeNote } from "../notes-model";
import { duplicatePath, incomingConnections, renamePath } from "../path-model";
import { nextPathId, removePath } from "../definition-model";
import type { OnEdit } from "../use-definition-history";
import type { Definition } from "../types";
import type { BuilderTools } from "./builder-tools";
import { useHistory } from "./history-context";

const P = "engage.workflows.extras.paths";
const H = "engage.workflows.extras.history";

interface Options {
  definition: Definition;
  /** The workflow cannot be edited: the actions do nothing. */
  disabled: boolean;
  onChange: OnEdit;
  /** A path was just created for the author (a copy): bring it into view. */
  onReveal: (pathId: string) => void;
}

/** The path and note actions of the builder as one object (`BuilderTools`); while the workflow cannot be edited only the notes can be read. */
export function useBuilderToolsValue({ definition, disabled, onChange, onReveal }: Options): BuilderTools {
  const { t } = useTranslation();
  const history = useHistory();
  const titleOf = (source: Definition, id: string): string => {
    const label = source.paths[id]?.label;
    return `${t("engage.workflows.builder.path", { id })}${label ? ` · ${label}` : ""}`;
  };

  const duplicate = (pathId: string) => {
    const source = definition.paths[pathId];
    if (!source) return;
    const copyLabel = t(`${P}.copyName`, { name: source.label ?? t("engage.workflows.builder.path", { id: pathId }) });
    const copy = duplicatePath(definition, pathId, copyLabel);
    if (!copy) {
      toast.warning(t(`${P}.full`, { max: WORKFLOW_LIMITS.paths }));
      return;
    }
    onChange(copy.definition, { label: t(`${H}.duplicatePath`, { path: titleOf(definition, pathId) }) });
    toast.success(t(`${P}.duplicated`, { path: titleOf(copy.definition, copy.pathId) }));
    onReveal(copy.pathId);
  };

  const remove = (pathId: string) => {
    if (pathId === WORKFLOW_FIRST_PATH) return;
    const next = removePath(definition, pathId);
    onChange(next, { label: t(`${H}.deletePath`, { path: titleOf(definition, pathId) }) });
    const undo = history ? { action: { label: t(`${H}.undo`), onClick: () => { history.undoIfCurrent(next); } } } : undefined;
    toast.success(t(`${P}.deleted`, { path: titleOf(definition, pathId) }), undo);
  };

  return {
    editable: !disabled,
    canAddPath: nextPathId(definition) !== null,
    incomingCount: (pathId) => incomingConnections(definition, pathId).length,
    rename: (pathId, label) => { onChange(renamePath(definition, pathId, label)); },
    duplicate,
    remove,
    notesAt: (pathId) => notesAt(definition, pathId),
    canAddNote: canAddNote(definition),
    addNote: (pathId, text) => {
      const added = addNote(definition, text, pathId ?? undefined);
      if (!added) {
        toast.warning(t("engage.workflows.extras.notes.full", { max: WORKFLOW_NOTES.max }));
        return null;
      }
      onChange(added.definition, { label: t(`${H}.addNote`) });
      return added.id;
    },
    editNote: (id, text) => { onChange(editNote(definition, id, text), { coalesceKey: `note:${id}`, label: t(`${H}.editNote`) }); },
    removeNote: (id) => { onChange(removeNote(definition, id), { label: t(`${H}.deleteNote`) }); },
  };
}
